'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ChevronDown, Search } from 'lucide-react';
import { BookDemoStart as Icon_BookDemoStart } from "@/components/customer/icons/BookDemoStart";
import { BookDemoworld as Icon_BookDemoworld } from "@/components/customer/icons/BookDemoworld";
const SvgIcons = { BookDemoStart: Icon_BookDemoStart, BookDemoworld: Icon_BookDemoworld };
import ReCAPTCHA from 'react-google-recaptcha';
import toast from 'react-hot-toast';
import Select from 'react-select';
import TrustedPartners from "../TrustedPartners";
import confetti from 'canvas-confetti';
import party from 'party-js';
import { getImageUrl, getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import GlobalPartnersCount from "@/components/GlobalPartnersCount";
import { trackEvent } from "@/lib/metaPixel";

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface VisitorData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: string;
  longitude: string;
  org: string;
}

interface BookedSlot {
  from_time: string;
  to_time: string;
}

// The calendar grid runs MON..SUN, so column 5 is Saturday and 6 is Sunday.
// Neither is a working day, so neither can be booked.
const isWeekendColumn = (colIdx: number) => colIdx === 5 || colIdx === 6;

export const BookDemoView = () => {
  // State declarations
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(now);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [timeZone, setTimeZone] = useState('Asia/Kolkata');
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    country: '',
    countryCode: '',
  });
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    mobile?: string;
    email?: string;
    captcha?: string;
    honeypot?: string;
    country?: string;
  }>({});
  const [showGuestInput, setShowGuestInput] = useState(false);
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [guestInput, setGuestInput] = useState('');
  const [description, setDescription] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [notification, setNotification] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [duration, setDuration] = useState<number>(30);
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [visitorData, setVisitorData] = useState<VisitorData>({
    city: 'Unknown',
    region: 'Unknown',
    country: 'Unknown',
    postal: 'Unknown',
    latitude: 'Unknown',
    longitude: 'Unknown',
    org: 'Unknown',
  });
  const recaptchaSiteKey = '6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-';
  const assetscountries =
    getFlagUrl();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch countries
  useEffect(() => {
    setIsCountryLoading(true);
    fetch("/api/countries")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data: Country[]) => {
        setCountries(Array.isArray(data) ? data : []);
        setIsCountryLoading(false);
        const defaultCountry = data.find((c: Country) => c.calling_code === 91);
        if (defaultCountry) {
          setForm((prev) => ({
            ...prev,
            country: defaultCountry.id.toString(),
            countryCode: `+${defaultCountry.calling_code}`,
          }));
        }
      })
      .catch((error) => {
        console.error('Error fetching countries:', error);
        notifyerror('Failed to load country codes');
        setIsCountryLoading(false);
      });
  }, []);

  // Fetch visitor data and set default country
  useEffect(() => {
    if (countries.length > 0) {
      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => res.json())
        .then((data) => {
          setVisitorData({
            city: data.city || 'Unknown',
            region: data.region || 'Unknown',
            country: data.country_code || 'Unknown',
            postal: data.postal || 'Unknown',
            latitude: data.latitude?.toString() || 'Unknown',
            longitude: data.longitude?.toString() || 'Unknown',
            org: data.org || 'Unknown',
          });

          const matchingCountry = countries.find(
            (country) => country.short_code === data.country_code,
          );
          if (matchingCountry) {
            setForm((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `+${matchingCountry.calling_code}`,
            }));
          } else {
            const defaultCountry = countries.find(
              (country) => country.calling_code === 91,
            );
            if (defaultCountry) {
              setForm((prev) => ({
                ...prev,
                country: defaultCountry.id.toString(),
                countryCode: `+${defaultCountry.calling_code}`,
              }));
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching visitor data:', error);
          setVisitorData({
            city: 'Unknown',
            region: 'Unknown',
            country: 'Unknown',
            postal: 'Unknown',
            latitude: 'Unknown',
            longitude: 'Unknown',
            org: 'Unknown',
          });
          const defaultCountry = countries.find(
            (country) => country.calling_code === 91,
          );
          if (defaultCountry) {
            setForm((prev) => ({
              ...prev,
              country: defaultCountry.id.toString(),
              countryCode: `+${defaultCountry.calling_code}`,
            }));
          }
        });
    }
  }, [countries]);

  // Calendar setup
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());

  const getDaysInMonth = (month: number, year: number) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month: number, year: number) =>
    new Date(year, month, 1).getDay();
  const getMonthLabel = (month: number, year: number) =>
    new Date(year, month, 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });

  const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
  const weekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const calendarGrid: (number | null)[][] = [];
  let week: (number | null)[] = [];
  let dayCounter = 1;
  let startDay = firstDay === 0 ? 6 : firstDay - 1;

  for (let i = 0; i < 7; i++) {
    week.push(i < startDay ? null : dayCounter++);
  }
  calendarGrid.push(week);

  while (dayCounter <= daysInMonth) {
    week = [];
    for (let i = 0; i < 7; i++) {
      week.push(dayCounter > daysInMonth ? null : dayCounter++);
    }
    calendarGrid.push(week);
  }

  // Time slots
  const getTimeSlots = () => {
    const slots: { from: string; to: string; label: string }[] = [];
    let start = new Date();
    start.setHours(10, 0, 0, 0);
    const endHour = 20;
    const increment = duration;

    for (
      let time = start.getTime();
      time < start.setHours(endHour, 0, 0, 0);
      time += increment * 60 * 1000
    ) {
      const slot = new Date(time);
      const hour = slot.getHours();
      if (hour >= 13 && hour < 14) continue;
      const label = `${slot.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone })} - ${new Date(slot.getTime() + increment * 60 * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone })}`;
      const from = slot.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const to = new Date(
        slot.getTime() + increment * 60 * 1000,
      ).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      slots.push({ from, to, label });
    }
    return slots;
  };
  const timeSlots = getTimeSlots();

  const isSlotBooked = (from: string, to: string) =>
    bookedSlots.some(
      (slot) =>
        slot.from_time.slice(0, 5) === from && slot.to_time.slice(0, 5) === to,
    );

  const isPastSlot = (from: string) => {
    if (!selectedDate) return false;
    const today = new Date();
    const selected = new Date(selectedDate);
    if (
      selected.getFullYear() !== today.getFullYear() ||
      selected.getMonth() !== today.getMonth() ||
      selected.getDate() !== today.getDate()
    )
      return false;
    const [hour, minute] = from.split(':').map(Number);
    const slotTime = new Date();
    slotTime.setHours(hour, minute, 0, 0);
    return slotTime <= today;
  };

  const handlePrevMonth = () => {
    if (
      calendarYear > now.getFullYear() ||
      (calendarYear === now.getFullYear() && calendarMonth > now.getMonth())
    ) {
      setCalendarMonth((prev) => (prev === 0 ? 11 : prev - 1));
      setCalendarYear((prev) => (calendarMonth === 0 ? prev - 1 : prev));
    }
  };

  const handleNextMonth = () => {
    setCalendarMonth((prev) => (prev === 11 ? 0 : prev + 1));
    setCalendarYear((prev) => (calendarMonth === 11 ? prev + 1 : prev));
  };

  const handleDateClick = (day: number | null, colIdx: number) => {
    if (!day || isWeekendColumn(colIdx) || isPastDate(day, calendarMonth, calendarYear))
      return;
    const date = new Date(Date.UTC(calendarYear, calendarMonth, day, 0, 0, 0));
    setSelectedDate(date);
    setSelectedTime(null);
    setShowCalendar(false);
    setShowTimeSlots(true);
    fetchBookedSlots(date);
  };

  const handleChangeDate = () => {
    setShowCalendar(true);
    setShowTimeSlots(false);
    setSelectedTime(null);
  };

  const notify = (msg: string) =>
    toast.success(msg, {
      style: {
        border: '1px solid #00b852',
        padding: '16px',
        color: '#00b852',
        zIndex: 10000,
      },
      iconTheme: { primary: '#00b852', secondary: '#FFFAEE' },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: '1px solid #cc0000',
        padding: '16px',
        color: '#cc0000',
        zIndex: 10000,
      },
      iconTheme: { primary: '#cc0000', secondary: '#FFFAEE' },
    });

  const handleCustomConfetti = () => {
    if (buttonRef.current) {
      party.confetti(buttonRef.current, {
        count: party.variation.range(20, 40),
      });
    }
  };

  const fetchBookedSlots = async (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      setBookedSlots([]);
      notifyerror('Please select today or a future date.');
      return;
    }
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const payload = { date: date.toISOString().slice(0, 10) };
    try {
      const response = await fetch(
        getApiUrl('/meeting/bookedslots'),
        {
          method: 'POST',
          headers: myHeaders,
          body: JSON.stringify(payload),
        },
      );
      const result = await response.json();
      if (result?.success === false && result?.error) {
        setBookedSlots([]);
        notifyerror(result.error);
      } else {
        setBookedSlots(result?.data?.slots_booked || []);
      }
    } catch (error) {
      setBookedSlots([]);
      notifyerror('Failed to fetch slots. Please try again.');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBookedSlots(now);
  }, []);

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    setShowCalendar(false);
    setShowTimeSlots(false);
    setShowForm(true);
  };

  const handleDurationChange = (minutes: number) => {
    setDuration(minutes);
    setSelectedTime(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    let sanitized = value;
    if (name === 'name') {
      sanitized = value.replace(/[^a-zA-Z\s\-'.]/g, '').slice(0, 100);
    } else if (name === 'mobile') {
      const maxLen =
        countries.find((c) => c.id.toString() === form.country)?.phone_length ||
        15;
      sanitized = value.replace(/\D/g, '').slice(0, maxLen);
    }
    setForm((prev) => ({ ...prev, [name]: sanitized }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAddGuest = () => {
    if (guestInput.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestInput)) {
      setGuestEmails((prev) => [...prev, guestInput.trim()]);
      setGuestInput('');
    } else {
      notifyerror('Enter Valid Email');
    }
  };

  const handleRemoveGuest = (email: string) => {
    setGuestEmails((prev) => prev.filter((g) => g !== email));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormErrors((prev) => ({ ...prev, captcha: '' }));
  };

  const validateForm = () => {
    const errors: typeof formErrors = {};
    if (!form.name.trim()) errors.name = 'Full name is required';
    if (!form.mobile.trim()) errors.mobile = 'Mobile number is required';
    else if (
      countries.find((c) => c.id.toString() === form.country)?.phone_length &&
      form.mobile.length !==
        countries.find((c) => c.id.toString() === form.country)?.phone_length
    ) {
      errors.mobile = `Mobile number must be ${countries.find((c) => c.id.toString() === form.country)?.phone_length} digits`;
    }
    if (!form.email.trim()) errors.email = 'Business email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.email = 'Invalid email format';
    if (!form.country) errors.country = 'Country code is required';
    if (honeypot.trim()) errors.honeypot = 'Spam detected';
    if (!recaptchaToken) errors.captcha = 'Please complete the reCAPTCHA';
    return errors;
  };

  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : '',
    calling_code: `+${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  const formatOptionLabel = ({
    calling_code,
    flag,
    country_name,
  }: CustomOption) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {flag && (
        <img
          src={flag}
          alt={`${country_name} flag`}
          style={{ width: '20px', borderRadius: '3px' }}
        />
      )}
      <span>{calling_code}</span>
    </div>
  );

  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setForm((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        mobile: '',
      }));
      setFormErrors((prev) => ({ ...prev, country: '' }));
    } else {
      setForm((prev) => ({ ...prev, country: '', countryCode: '' }));
    }
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#195AFF', '#EBF0FF', '#2BBF3F'],
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      setFormLoading(true);
      let from = '';
      let to = '';
      if (selectedTime) {
        const [startTime] = selectedTime.split(' - ');
        const [time, period] = startTime.split(' ');
        let [hour, minute] = time.split(':').map(Number);
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        from = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        let toHour = hour;
        let toMinute = minute + duration;
        if (toMinute >= 60) {
          toHour += Math.floor(toMinute / 60);
          toMinute = toMinute % 60;
        }
        to = `${toHour.toString().padStart(2, '0')}:${toMinute.toString().padStart(2, '0')}`;
      }
      const payload = {
        name: form.name,
        email: form.email,
        country_code: form.countryCode,
        mobile: form.mobile,
        date: selectedDate ? selectedDate.toISOString().slice(0, 10) : '',
        timezone: timeZone,
        meeting_time: { from, to },
        guest_emails: guestEmails,
        description,
        'g-recaptcha-response': recaptchaToken,
        honeypot,
      };

      try {
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');
        const response = await fetch(
          getApiUrl('/schedule/meeting'),
          {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(payload),
          },
        );
        const result = await response.json();
        if (result && result.success) {
          // Meta standard event for a booked appointment, with advanced
          // matching from what the visitor just typed.
          trackEvent(
            'Schedule',
            { content_name: 'Book a Demo', content_category: 'demo' },
            {
              em: form.email,
              ph: `${form.countryCode || ''}${form.mobile || ''}`,
              fn: form.name,
              country: form.country,
            },
          );
          notify('Meeting Scheduled Successfully.');
          triggerConfetti();
          handleCustomConfetti();
          fetchBookedSlots(now);
          setShowForm(false);
          setShowCalendar(true);
          setShowTimeSlots(false);
          setForm({
            name: '',
            mobile: '',
            email: '',
            country: '',
            countryCode: '',
          });
          setGuestEmails([]);
          setDescription('');
          setRecaptchaToken(null);
          setHoneypot('');
          setSelectedDate(now);
          setSelectedTime(null);
        } else {
          notifyerror(
            result?.error || 'Failed to schedule event. Please try again.',
          );
        }
      } catch (error) {
        notifyerror('Failed to schedule event. Please try again.');
        console.error(error);
      } finally {
        setFormLoading(false);
      }
    } else {
      if (errors.captcha) {
        notifyerror(errors.captcha);
      }
    }
  };

  const isPastDate = (day: number, month: number, year: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(year, month, day) < today;
  };

  const isFormValid =
    form.name.trim() &&
    form.mobile.trim() &&
    form.email.trim() &&
    form.country.trim() &&
    form.countryCode.trim() &&
    selectedDate &&
    selectedTime &&
    description.trim() &&
    recaptchaToken;

  const isPrevMonthDisabled = () =>
    calendarYear < now.getFullYear() ||
    (calendarYear === now.getFullYear() && calendarMonth <= now.getMonth());

  const selectedDateLabel = selectedDate
    ? `${selectedDate.toLocaleDateString('en-US', { weekday: 'long' })} , ${selectedDate.getDate()}`
    : '';

  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: '8px 0 0 8px',
      border: `2px solid ${formErrors.mobile || formErrors.country ? '#EF4444' : '#CEDFFE'}`,
      borderRight: 'none',
      height: '40px',
      minWidth: '100px',
      backgroundColor: 'white',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
      color: '#090D28',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#090D28',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '300px',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#2563eb',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#E6E9FF',
        borderRadius: '3px',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#090D28',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
  };

  const TimeInput = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState({
      value: 'Asia/Kolkata',
      label: 'Indian Standard Time (IST)',
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const timeZones = [
      { label: 'Indian Standard Time - IST (+05:30)', value: 'Asia/Kolkata' },
      {
        label: 'British Summer Time / GMT - BST (+01:00)',
        value: 'Europe/London',
      },
      {
        label: 'Eastern Standard Time - EST (-05:00)',
        value: 'America/New_York',
      },
      {
        label: 'Central Standard Time - CST (-06:00)',
        value: 'America/Chicago',
      },
      {
        label: 'Mountain Standard Time - MST (-07:00)',
        value: 'America/Denver',
      },
      {
        label: 'Pacific Standard Time - PST (-08:00)',
        value: 'America/Los_Angeles',
      },
      { label: 'Japan Standard Time - JST (+09:00)', value: 'Asia/Tokyo' },
      { label: 'China Standard Time - CST (+08:00)', value: 'Asia/Shanghai' },
      {
        label: 'Australian Eastern Standard Time - AEST (+10:00)',
        value: 'Australia/Sydney',
      },
      { label: 'Greenwich Mean Time - GMT (+00:00)', value: 'Etc/GMT' },
    ];

    const filteredTimezones = timeZones.filter((timezone) =>
      timezone.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredTimezones.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredTimezones.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredTimezones.length
          ) {
            setSelectedTimezone(filteredTimezones[highlightedIndex]);
            setTimeZone(filteredTimezones[highlightedIndex].value);
            setIsOpen(false);
            setSearchQuery('');
            setHighlightedIndex(-1);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
          break;
      }
    };

    const handleTimezoneSelect = (timezone: (typeof timeZones)[0]) => {
      setSelectedTimezone(timezone);
      setTimeZone(timezone.value);
      setIsOpen(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
    };

    return (
      <div className='mx-auto w-full max-w-md'>
        <div className='space-y-2'>
          <label className='mb-[4px] font-Schibsted_Grotesk text-[12px] font-normal leading-[100%] text-[#677492]'>
            Time Zone
          </label>
          <div className='relative' ref={dropdownRef}>
            <div
              className={`flex h-12 w-full cursor-pointer items-center justify-between rounded-[8px] px-3 text-left font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-[#090D28] ${isOpen ? 'rounded-b-none' : 'rounded-b-[8px]'} border-[0.8px] border-solid border-[#D1D7DD] bg-white`}
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={handleKeyDown}
            >
              <SvgIcons.BookDemoworld />
              <span className='truncate font-Schibsted_Grotesk'>{selectedTimezone.label}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isOpen && 'rotate-180'}`}
              />
            </div>
            {isOpen && (
              <div className='absolute left-0 right-0 top-full z-50 max-h-64 overflow-hidden rounded-md rounded-t-none border border-t-0 border-gray-200 bg-white shadow-lg'>
                <div className='w-full self-stretch rounded-[8px] rounded-t-none border-[0.8px] border-b border-t-0 border-[#D1D7DD] p-2 '>
                  <div className='relative'>
                    <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400' />
                    <input
                      ref={searchInputRef}
                      placeholder='Select Time Zone'
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setHighlightedIndex(0);
                      }}
                      onKeyDown={handleKeyDown}
                      className='h-10 w-full self-stretch rounded-[8px] border-[0.8px] border-[#D1D7DD] font-Schibsted_Grotesk p-2 pl-10'
                    />
                  </div>
                </div>
                <div className='demo-slider-scroll max-h-48 overflow-y-scroll'>
                  {filteredTimezones.length === 0 ? (
                    <div className='px-3 py-2 text-sm text-gray-500'>
                      No timezones found
                    </div>
                  ) : (
                    filteredTimezones.map((timezone, index) => (
                      <button
                        key={timezone.value}
                        type='button'
                        className={`w-full px-3 py-2 text-left text-sm font-Schibsted_Grotesk hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                          index === highlightedIndex && 'bg-gray-50'
                        } ${selectedTimezone.value === timezone.value && 'bg-blue-50 text-blue-700'}`}
                        onClick={() => handleTimezoneSelect(timezone)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {timezone.label}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <style jsx>{`
        .demo-slider-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .demo-slider-scroll::-webkit-scrollbar-track {
          background: #f1f4ff;
          border-radius: 4px;
        }
        .demo-slider-scroll::-webkit-scrollbar-thumb {
          background: #8291b6;
          border-radius: 4px;
        }
        .demo-slider-scroll::-webkit-scrollbar-thumb:hover {
          background: #0f3aa8;
        }
      `}</style>

      <div className='bg-[radial-gradient(139.52%_77.57%_at_49.32%_11.49%,#1042C0_0%,#0A215C_100%)] py-[40px] md:py-[60px]'>
        <div className='block items-start justify-center gap-[55px] px-[14px] md:container md:flex md:px-4'>
          <div className='w-full md:w-1/2'>
            <div className='flex items-start justify-center gap-3'>
              <div className='relative w-full rounded-[12px] bg-white shadow-lg md:w-fit'>
                {notification && (
                  <div
                    className={`mb-4 rounded p-2 text-white ${notification.includes('success') ? 'bg-green-500' : 'bg-red-500'}`}
                  >
                    {notification}
                  </div>
                )}
                {!showForm ? (
                  <div className='flex flex-col sm:flex-row md:flex-col lg:flex-row'>
                    <div className='w-full border-r-0 p-[24px] sm:border-r md:border-r-0 lg:border-r'>
                      <div className='flex items-center justify-between'>
                        <div className='mb-[24px] flex w-full items-center justify-center gap-[12px]'>
                          <button
                            aria-label='calendar backward'
                            className='group flex aspect-[1/1] h-[36px] w-[36px] items-center justify-center rounded-[8px] bg-[#EBF0FF] p-[8px] hover:text-[#195AFF]'
                            onClick={handlePrevMonth}
                            type='button'
                            disabled={isPrevMonthDisabled()}
                            style={{
                              opacity: isPrevMonthDisabled() ? 0.5 : 1,
                              cursor: isPrevMonthDisabled()
                                ? 'not-allowed'
                                : 'pointer',
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='icon icon-tabler icon-tabler-chevron-left'
                              width='24'
                              height='24'
                              viewBox='0 0 24 24'
                              strokeWidth='1.5'
                              stroke='currentColor'
                              fill='none'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <path
                                stroke='none'
                                d='M0 0h24v24H0z'
                                fill='none'
                              />
                              <polyline points='15 6 9 12 15 18' />
                            </svg>
                          </button>
                          <span className='font-Schibsted_Grotesk font-bold text-[16px] leading-[100%] text-[#090D28]'>
                            {getMonthLabel(calendarMonth, calendarYear)}
                          </span>
                          <button
                            aria-label='calendar forward'
                            className='group flex aspect-[1/1] h-[36px] w-[36px] items-center justify-center rounded-[8px] bg-[#EBF0FF] p-[8px]'
                            onClick={handleNextMonth}
                            type='button'
                            disabled={calendarYear > now.getFullYear() + 1}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='21'
                              height='20'
                              viewBox='0 0 21 20'
                              fill='none'
                            >
                              <path
                                d='M8 15L13 10L8 5'
                                className='stroke-black group-hover:stroke-[#195AFF]'
                                strokeWidth='1.25'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className='flex items-center justify-between overflow-x-auto'>
                        <table className='w-full'>
                          <thead>
                            <tr>
                              {weekDays.map((d) => (
                                <th key={d}>
                                  <div className='mb-[8px] flex w-full justify-center'>
                                    <p className='font-Schibsted_Grotesk text-[12px] font-normal leading-[100%] text-[#090D28]'>
                                      {d}
                                    </p>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {calendarGrid.map((week, rowIdx) => (
                              <tr key={rowIdx}>
                                {week.map((day, colIdx) => {
                                  const isWeekend = isWeekendColumn(colIdx);
                                  const isSelected =
                                    selectedDate &&
                                    day &&
                                    selectedDate.getDate() === day &&
                                    selectedDate.getMonth() === calendarMonth &&
                                    selectedDate.getFullYear() === calendarYear;
                                  const disabled =
                                    !day ||
                                    isWeekend ||
                                    isPastDate(
                                      day,
                                      calendarMonth,
                                      calendarYear,
                                    );
                                  return (
                                    <td
                                      key={colIdx}
                                      className='ps-[4px] pt-[4px]'
                                      onClick={() =>
                                        !disabled &&
                                        handleDateClick(day, colIdx)
                                      }
                                      style={{
                                        pointerEvents: disabled
                                          ? 'none'
                                          : 'auto',
                                        opacity: disabled ? 0.5 : 1,
                                      }}
                                    >
                                      <div 
                                        className={`flex w-[36px] cursor-pointer text-[#FFF] justify-center gap-7 rounded-[20px]
                                          ${isWeekend ? '' : 'hover:bg-[#195AFF] hover:text-[#FFF]'}
                                          ${isSelected ? 'rounded-[20px] bg-[#195AFF] text-[#FFF]' : 'bg-[#EBF0FF]'}`}
                                        style={{
                                          color: isWeekend ? '#000' : undefined,
                                          fontWeight: isSelected
                                            ? 'bold'
                                            : undefined,
                                        }}
                                      >
                                        {day ? (
                                          <p
                                            className={`flex aspect-[1/1] w-[36px] items-center justify-center px-[0px] font-Schibsted_Grotesk text-[14px] hover:text-[#FFF] font-normal ${isSelected ? 'text-white' : isWeekend ? 'text-[#000]' : 'text-[#195AFF]'}`}
                                          >
                                            {day}
                                          </p>
                                        ) : null}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className='mt-9'>
                        <TimeInput />
                      </div>
                    </div>
                    <div className='relative w-full p-[24px] sm:w-1/2 md:w-full lg:w-1/2'>
                      <div className='mb-[24px] flex items-center justify-between'>
                        <p className='my-2 mr-4 flex-1 whitespace-nowrap text-center font-Schibsted_Grotesk text-[16px] font-normal text-[#090D28]'>
                          {selectedDate ? selectedDateLabel : 'Select a date'}
                        </p>
                        <div className='flex items-center rounded-[6px] bg-[#F0F4FF] p-[2px]'>
                          <button
                            className={`flex items-center justify-center gap-[10px] whitespace-nowrap rounded-[4px] !border !border-solid p-2 font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-[#195AFF] ${duration === 30 ? '!border-[#195AFF]' : 'border-transparent'}`}
                            onClick={() => handleDurationChange(30)}
                          >
                            30 Mins
                          </button>
                          <button
                            className={`flex items-center justify-center gap-[10px] whitespace-nowrap rounded-[4px] !border !border-solid p-2 font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-[#195AFF] ${duration === 45 ? '!border-[#195AFF]' : 'border-transparent'}`}
                            onClick={() => handleDurationChange(45)}
                          >
                            45 Mins
                          </button>
                        </div>
                      </div>
                      <div className='relative'>
                        <div className='demo-slider-scroll h-[300px] overflow-y-scroll pr-3 pb-14'>
                          {selectedDate && (
                            <>
                              <p className='mb-[8px] font-Schibsted_Grotesk text-[12px] font-normal not-italic leading-[100%] text-[#677492]'>
                                Morning
                              </p>
                              {timeSlots.map((slot, idx) => {
                                const disabled =
                                  isSlotBooked(slot.from, slot.to) ||
                                  isPastSlot(slot.from);
                                const isEvening =
                                  new Date(
                                    `2000-01-01 ${slot.from}`,
                                  ).getHours() >= 14;
                                return (
                                  <div key={slot.from}>
                                    {idx > 0 &&
                                      isEvening &&
                                      !(
                                        new Date(
                                          `2000-01-01 ${timeSlots[idx - 1].from}`,
                                        ).getHours() >= 14
                                      ) && (
                                        <p className='mb-[8px] font-Schibsted_Grotesk text-[12px] font-normal not-italic leading-[100%] text-[#677492]'>
                                          Evening
                                        </p>
                                      )}
                                    <div
                                      className={`mb-[8px] flex cursor-pointer items-center justify-center gap-[10px] self-stretch rounded-[4px] border p-[10px] font-Schibsted_Grotesk text-[14px] font-normal transition hover:bg-[#195AFF] hover:text-white
                                        ${selectedTime === slot.label ? 'border-[#195AFF] bg-[#195AFF] text-white' : 'border-[#195AFF] text-[#195AFF]'}
                                        ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                      onClick={() =>
                                        !disabled && handleTimeClick(slot.label)
                                      }
                                      style={{
                                        pointerEvents: disabled
                                          ? 'none'
                                          : 'auto',
                                      }}
                                    >
                                      {slot.label}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          <div className='absolute bottom-0 h-[50px] w-full shrink-0 bg-gradient-to-b from-white/0 to-white'></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='p-[24px]'>
                    <p className='mb-[10px] font-NeueMontreal font-bold text-[18px] text-[#090D28]'>
                      Personal Details
                    </p>
                    <form onSubmit={handleFormSubmit} autoComplete='off'>
                      <input
                        type='text'
                        name='honeypot'
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        style={{ display: 'none' }}
                        tabIndex={-1}
                        autoComplete='off'
                        className='font-Schibsted_Grotesk'
                      />
                      <div className='mb-4'>
                        <label className='font-Schibsted_Grotesk text-[14px] font-normal text-[#090D28]'>
                          Full name *
                        </label>
                        <input
                          className={`flex h-[40px] font-Schibsted_Grotesk w-full items-center gap-[10px] self-stretch rounded-[8px] border-[2px] bg-white px-[16px] ${formErrors.name ? 'border-red-500' : 'border-[#CEDFFE]'} focus:border-[#1D31C3] focus:outline-none`}
                          type='text'
                          name='name'
                          placeholder='Enter your name'
                          value={form.name}
                          onChange={handleFormChange}
                        />
                        {formErrors.name && (
                          <p className='mt-1 text-xs text-red-500'>
                            {formErrors.name}
                          </p>
                        )}
                      </div>
                      <div className='mb-4'>
                        <label className='font-Schibsted_Grotesk text-[14px] font-normal text-[#090D28]'>
                          Mobile Number *
                        </label>
                        <div className='flex items-center'>
                          <div className='relative'>
                            <Select
                              name='country'
                              options={customOptions}
                              value={
                                customOptions.find(
                                  (option) => option.value === form.country,
                                ) || null
                              }
                              onChange={handleCountryChange}
                              formatOptionLabel={formatOptionLabel}
                              styles={countryCodeStyles}
                              placeholder='+91'
                              className='font-Schibsted_Grotesk'
                              isDisabled={isCountryLoading}
                              isLoading={isCountryLoading}
                            />
                            <svg
                              className='absolute right-2 top-1/2 -translate-y-1/2 transform'
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                            >
                              <path
                                d='M5 7.5L10 12.5L15 7.5'
                                stroke='black'
                                strokeWidth='1.25'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                          <input
                            className={`h-[40px] font-Schibsted_Grotesk w-full items-center gap-[10px] self-stretch rounded-[8px] rounded-l-none border-[2px] bg-white px-[16px] py-[10px] ${formErrors.mobile || formErrors.country ? 'border-red-500' : 'border-l-0 border-[#CEDFFE]'} focus:border-[#195AFF] focus:outline-none`}
                            type='text'
                            name='mobile'
                            placeholder="Enter mobile number"
                            inputMode="numeric"
                            maxLength={countries.find((c) => c.id.toString() === form.country)?.phone_length || 10}
                            value={form.mobile}
                            onChange={handleFormChange}
                          />
                        </div>
                        {(formErrors.mobile || formErrors.country) && (
                          <p className='mt-1 text-xs text-red-500'>
                            {formErrors.mobile || formErrors.country}
                          </p>
                        )}
                      </div>
                      <div className='mb-4'>
                        <label className='font-Schibsted_Grotesk text-[14px] font-normal text-[#090D28]'>
                          Business email *
                        </label>
                        <input
                          className={`flex h-[40px] font-Schibsted_Grotesk w-full items-center gap-[10px] self-stretch rounded-[8px] border-[2px] bg-white px-[16px] py-[10px] ${formErrors.email ? 'border-red-500' : 'border-[#CEDFFE]'} focus:border-[#1D31C3] focus:outline-none`}
                          type='email'
                          name='email'
                          placeholder='Enter your business email'
                          value={form.email}
                          onChange={handleFormChange}
                        />
                        {formErrors.email && (
                          <p className='mt-1 text-xs text-red-500 font-Schibsted_Grotesk'>
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                      <button
                        type='button'
                        onClick={() => setShowGuestInput(!showGuestInput)}
                        className='flex items-center justify-center font-Schibsted_Grotesk gap-[10px] rounded-[20px] border-[0.8px] border-[#195AFF] bg-[#EFF4FF] px-[16px] py-[8px] font-Schibsted_Grotesk text-[14px] font-normal text-[#195AFF]'
                      >
                        Add Guests
                      </button>
                      {showGuestInput && (
                        <div className='mb-4 mt-2'>
                          <label className='font-Schibsted_Grotesk text-[14px] font-normal text-[#090D28]'>
                            Guest email(s)
                          </label>
                          <div className='mt-1 flex gap-2'>
                            <input
                              className='flex h-[40px] w-full font-Schibsted_Grotesk items-center gap-[10px] self-stretch rounded-[8px] border-[2px] border-[#CEDFFE] bg-white px-[16px]'
                              type='email'
                              placeholder='Enter guest email'
                              value={guestInput}
                              onChange={(e) => setGuestInput(e.target.value)}
                            />
                            <button
                              type='button'
                              className='rounded-[8px] bg-[#195AFF] font-Schibsted_Grotesk px-4 text-white'
                              onClick={handleAddGuest}
                            >
                              Add
                            </button>
                          </div>
                          <div className='mt-2 flex flex-wrap gap-2'>
                            {guestEmails.map((email) => (
                              <span
                                key={email}
                                className='flex items-center gap-2 rounded-[20px] bg-[#EFF4FF] px-3 py-1 text-[#195AFF]'
                              >
                                {email}
                                <button
                                  type='button'
                                  onClick={() => handleRemoveGuest(email)}
                                  className='font-bold text-[#195AFF] font-Schibsted_Grotesk'
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className='mb-4'>
                        <label className='font-Schibsted_Grotesk text-[14px] font-normal text-[#090D28]'>
                          Share any details that will help us get ready for our
                          meeting
                        </label>
                        <textarea
                          className='flex w-full self-stretch rounded-[8px] border-[2px] border-[#CEDFFE] bg-white px-[16px] py-[10px] focus:border-[#1D31C3] focus:outline-none font-Schibsted_Grotesk text-[14px] resize-none'
                          rows={3}
                          name='description'
                          placeholder='Write Here...'
                          value={description}
                          maxLength={500}
                          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                        />
                        <p className='mt-1 text-right text-xs text-[#677492] font-Schibsted_Grotesk'>{description.length}/500</p>
                      </div>
                      <div className='mb-4 flex items-center justify-center'>
                        <ReCAPTCHA
                          sitekey={recaptchaSiteKey}
                          onChange={handleRecaptchaChange}
                        />
                        {formErrors.captcha && (
                          <p className='mt-1 text-xs text-red-500 font-Schibsted_Grotesk'>
                            {formErrors.captcha}
                          </p>
                        )}
                        {formErrors.honeypot && (
                          <p className='mt-1 text-xs text-red-500 font-Schibsted_Grotesk'>
                            {formErrors.honeypot}
                          </p>
                        )}
                      </div>
                      <div className='flex items-center justify-between'>
                        <button
                          type='button'
                          onClick={() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            setShowForm(false);
                            setShowCalendar(true);
                            setShowTimeSlots(true);
                            setSelectedTime(null);
                          }}
                          className='flex items-center justify-center font-Schibsted_Grotesk rounded-[8px] bg-[#EFF4FF] px-8 py-3 font-medium text-[16px] not-italic leading-[100%] text-[#195AFF]'
                        >
                          Back
                        </button>
                        <button
                          type='submit'
                          ref={buttonRef}
                          disabled={!isFormValid || formLoading}
                          className={`w-fit rounded-[8px] px-8 py-3 font-Schibsted_Grotesk font-medium text-[16px] transition
                            ${
                              !isFormValid || formLoading
                                ? 'cursor-not-allowed bg-gray-300 text-gray-600'
                                : 'cursor-pointer bg-[#195AFF] text-white hover:bg-[#0d4fd1]'
                            }`}
                        >
                          {formLoading ? 'Scheduling...' : 'Schedule'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='w-full pt-[48px] sm:pt-0 md:w-1/2 md:pt-[48px] lg:pt-0'>
            <h1 className='mb-[16px] font-NeueMontreal text-[36px] font-normal leading-[100%] text-white md:text-[44px]'>
              Book a{' '}
              <span className='font-NeueMontreal text-[36px] font-normal leading-[100%] text-[#F9F07B] md:text-[44px]'>
                Free Product Demo
              </span>
            </h1>
            <p className='font-Schibsted_Grotesk text-[16px] leading-[150%] text-[#EAEFFC] md:text-[18px]'>
              Get a live, personalized walkthrough of our solution. Discover how
              we can help you save time, increase revenue, and streamline
              operations.
            </p>
            <ul className='m-0 my-[24px] p-0'>
              <li className='mb-[24px] flex items-center gap-[8px] font-Schibsted_Grotesk text-[18px] leading-[120%] text-white md:text-[20px]'>
                <SvgIcons.BookDemoStart />
                Personalized Walkthrough
              </li>
              <li className='mb-[24px] flex items-center gap-[8px] font-Schibsted_Grotesk text-[18px] leading-[120%] text-white md:text-[20px]'>
                <SvgIcons.BookDemoStart />
                Clear ROI Insights
              </li>
              <li className='mb-[24px] flex items-center gap-[8px] font-Schibsted_Grotesk text-[18px] leading-[120%] text-white md:text-[20px]'>
                <SvgIcons.BookDemoStart />
                Live Product Tour
              </li>
              <li className='flex items-center gap-[8px] font-Schibsted_Grotesk text-[18px] leading-[120%] text-white md:text-[20px]'>
                <SvgIcons.BookDemoStart />
                Expert Guidance - No Pressure
              </li>
            </ul>
            <section className='mb-[60px] flex items-center justify-start gap-3'>
              <div>
                <div>
                  <a href='https://businessfirms.co/' target='_blank' aria-label="Business Firms website" rel="noopener noreferrer">
                    <Image
                      src={getImageUrl("/vilvabusiness/website/common/otherallimages/Frame%20642%20%282%29.webp")}
                      alt='Business Firms logo'
                      width={270}
                      height={140}
                    />
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <TrustedPartners />
    </div>
  );
};

export default BookDemoView;
